import { useCallback, useEffect, useMemo, useState } from 'react';
import { createLogger } from '@/lib/logger';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useProfileStore } from '@/stores/useProfileStore';
import { normalizeTier, hasAtLeast, resolveCapabilities, type Capabilities } from '@/lib/subscription';

const log = createLogger('USEPRO');

const DEV_PRO_OVERRIDE_KEY = 'dev_pro_override';

export type ProSource = 'dev_override' | 'subscription' | 'none';

export type UseProResult = {
  isPro: boolean;
  isCoachPro: boolean;
  isOrgPro: boolean;
  source: ProSource;
  capabilities: Capabilities;
  devOverride: boolean;
  setDevOverride: (enabled: boolean) => Promise<void>;
};

export function usePro(): UseProResult {
  const [devOverride, setDevOverrideState] = useState(false);
  const profile = useProfileStore((s) => s.profile);

  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(DEV_PRO_OVERRIDE_KEY);
        if (!isActive) return;
        setDevOverrideState(stored === 'true');
      } catch (error) {
        log.warn('[usePro] Failed to read dev override', error);
      }
    })();
    return () => {
      isActive = false;
    };
  }, []);

  const setDevOverride = useCallback(async (enabled: boolean) => {
    if (!__DEV__) return;
    setDevOverrideState(enabled);
    try {
      await AsyncStorage.setItem(DEV_PRO_OVERRIDE_KEY, enabled.toString());
    } catch (error) {
      log.warn('[usePro] Failed to persist dev override', error);
    }
  }, []);

  const result = useMemo(() => {
    // Dev override takes precedence in __DEV__ builds
    if (devOverride && __DEV__) {
      const caps = resolveCapabilities('pro');
      return {
        isPro: true,
        isCoachPro: false,
        isOrgPro: false,
        source: 'dev_override' as ProSource,
        capabilities: caps,
        devOverride,
        setDevOverride,
      };
    }

    // Resolve real subscription tier from profile
    const tier = normalizeTier(profile);
    const caps = resolveCapabilities(tier);

    const isPro = hasAtLeast(tier, 'pro');
    const isCoachPro = hasAtLeast(tier, 'coach_pro');
    const isOrgPro = hasAtLeast(tier, 'org_pro');
    const source: ProSource = isPro ? 'subscription' : 'none';

    if (isPro) {
      log.info('[usePro] subscription active', { tier, source });
    }

    return {
      isPro,
      isCoachPro,
      isOrgPro,
      source,
      capabilities: caps,
      devOverride,
      setDevOverride,
    };
  }, [devOverride, profile, setDevOverride]);

  return result;
}
